/**
 * ChatInput Component
 *
 * A modern, professional chat input component with:
 * - Auto-resize textarea
 * - File attachments
 * - Thinking mode selector
 * - Web search toggle
 * - Mobile-friendly responsive design
 * - Skill and model selection
 *
 * BACKEND INTEGRATION GUIDE:
 *
 * When the backend APIs are ready, update the handleSend function to include
 * all the new state in the message payload:
 *
 * ```typescript
 * const payload = {
 *   message: message,
 *   thinkingMode: thinkingMode,      // Add this
 *   webSearchEnabled: webSearchEnabled, // Add this
 *   attachments: attachments.map(a => ({ // Add this
 *     id: a.id,
 *     name: a.name,
 *     size: a.size,
 *     type: a.type,
 *     // Include backend URL if available
 *     url: a.url,
 *   })),
 *   sessionId: sessionId,
 *   skill: skill,
 * };
 *
 * await streamMessage(payload.message, payload.skill, {
 *   thinkingMode: payload.thinkingMode,
 *   webSearchEnabled: payload.webSearchEnabled,
 *   attachments: payload.attachments,
 * });
 * ```
 *
 * Current implementation: Sends only message and skill (original behavior)
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Square, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { AttachmentButton } from "./AttachmentButton";
import { ThinkingModeSelector } from "./ThinkingModeSelector";
import { WebSearchToggle } from "./WebSearchToggle";
import { ModelSelector } from "./ModelSelector";
import type {
  ChatInputProps,
  ChatInputState,
  ThinkingMode,
  FileAttachment,
} from "./types";

export function ChatInput({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = "Type your message...",
  skill = "general",
  skills = [],
  onSkillChange,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingMode, setThinkingMode] = useState<ThinkingMode>("balanced");
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  /**
   * Auto-resize textarea based on content
   */
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate new height (max 200px for desktop, 150px for mobile)
    const maxHeight = window.innerWidth < 768 ? 150 : 200;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${newHeight}px`;
  }, [value]);

  /**
   * Handle sending the message with all options
   *
   * TODO: Update this function when backend is ready to accept:
   * - thinkingMode
   * - webSearchEnabled
   * - attachments
   */
  const handleSend = useCallback(async () => {
    const message = value.trim();
    if (!message || isStreaming || disabled) {
      return;
    }

    // Check if there are pending uploads
    const hasPendingUploads = attachments.some((a) => a.status === 'uploading');
    if (hasPendingUploads) {
      // TODO: Show toast/notification to user
      console.warn('Please wait for attachments to finish uploading');
      return;
    }

    setIsStreaming(true);

    // Prepare the complete input state
    const inputState: ChatInputState = {
      message,
      thinkingMode,
      webSearchEnabled,
      attachments,
    };

    try {
      // TODO: When backend is ready, send the full inputState
      // For now, we only send the message to maintain backward compatibility
      await onSend(inputState);

      // Clear input and attachments on successful send
      onChange("");
      setAttachments([]);
    } catch (error) {
      console.error("Failed to send message:", error);
      // TODO: Show error toast/notification
    } finally {
      setIsStreaming(false);
    }
  }, [value, isStreaming, disabled, attachments, thinkingMode, webSearchEnabled, onChange, onSend]);

  /**
   * Handle keyboard shortcuts
   * - Enter: Send message
   * - Shift + Enter: New line
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  /**
   * Stop the current streaming response
   * TODO: Integrate with actual stream stop function
   */
  const handleStop = useCallback(() => {
    // TODO: Call the actual stop streaming function
    setIsStreaming(false);
    console.log("Stop streaming requested");
  }, []);

  const skillLabel = skill.charAt(0).toUpperCase() + skill.slice(1).replace('/', ' ');
  const fullPlaceholder = `Using ${skillLabel}... ${placeholder}`;

  return (
    <div className="border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-4xl mx-auto px-3 md:px-4 lg:px-6 py-3 md:py-4 space-y-3">
        {/* Skill & Model Selector - Top bar */}
        {skills.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pb-2 border-b border-border/50">
            {/* Skill Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground hidden sm:inline">
                Skill:
              </span>
              <Select
                value={skill}
                onValueChange={(value) => onSkillChange?.(value)}
                disabled={disabled || isStreaming}
              >
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue placeholder="Select a skill" />
                </SelectTrigger>
                <SelectContent>
                  {skills.map((skillOption) => (
                    <SelectItem
                      key={skillOption.command}
                      value={skillOption.command}
                      className="text-xs"
                    >
                      {skillOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vertical divider on larger screens */}
            <div className="hidden sm:block w-px h-6 bg-border" />

            {/* Model Selector */}
            <ModelSelector />
          </div>
        )}

        {/* Thinking Mode & Web Search - Middle bar */}
        <div className="flex flex-wrap justify-between items-center gap-3 sm:gap-4">
          {/* Thinking Mode Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground hidden sm:inline">
              Thinking:
            </span>
            <ThinkingModeSelector
              value={thinkingMode}
              onChange={setThinkingMode}
              disabled={disabled || isStreaming}
            />
          </div>

          {/* Web Search Toggle */}
          <div className="flex items-center">
            <WebSearchToggle
              enabled={webSearchEnabled}
              onChange={setWebSearchEnabled}
              disabled={disabled || isStreaming}
            />
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Attachment Count Badge */}
          {attachments.length > 0 && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="hidden sm:inline">📎</span>
              <span>{attachments.length} file{attachments.length !== 1 ? 's' : ''} attached</span>
            </div>
          )}
        </div>

        {/* Input Area - Bottom bar (always visible) */}
        <div className="flex gap-2 md:gap-3 items-end">
          {/* Attachment Button */}
          <AttachmentButton
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            disabled={disabled || isStreaming}
          />

          {/* Text Input */}
          <div className="flex-1 relative min-w-0">
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={fullPlaceholder}
              disabled={disabled || isStreaming}
              className="min-h-[44px] md:min-h-[52px] max-h-[150px] md:max-h-[200px] resize-none rounded-xl md:rounded-2xl px-4 py-3 text-sm shadow-[0_2px_4px_0_rgba(0,0,0,0.05)] focus:shadow-[0_4px_8px_0_rgba(0,0,0,0.1)] transition-shadow"
              rows={1}
            />
            {isStreaming && (
              <div className="absolute right-3 bottom-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Send/Stop Button */}
          {isStreaming ? (
            <Button
              variant="outline"
              size="icon"
              onClick={handleStop}
              className="h-11 w-11 md:h-12 md:w-12 shrink-0 rounded-full"
              type="button"
              aria-label="Stop generation"
            >
              <Square className="h-5 w-5" />
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              disabled={!value.trim() || disabled}
              size="icon"
              className="h-11 w-11 md:h-12 md:w-12 shrink-0 rounded-full shadow-[0_2px_4px_0_rgba(0,0,0,0.1)] hover:shadow-[0_4px_8px_0_rgba(0,0,0,0.15)] transition-shadow"
              type="button"
              aria-label="Send message"
            >
              <Send className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Helper Text */}
        <div className="text-xs text-muted-foreground text-center hidden md:block">
          Press <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono">Enter</kbd> to send,
          <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono ml-1">Shift + Enter</kbd> for new line
        </div>
      </div>
    </div>
  );
}
